import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    TextField,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Collapse,
    IconButton,
} from "@mui/material";
import { Close as CloseIcon } from '@mui/icons-material';
import { Formik, Form } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router";
import Recaptcha from "../../Recaptcha";
import SmsKoduKontrolAPI, { SmsDogrulamaKontrolRequest } from "../../../apis/OB/SaveApi/SmsKoduKontrolAPI";
import SifremiUnuttumAPI, { SmsDogrulamaRequest } from "../../../apis/OB/SaveApi/SifremiUnuttumAPI";
import { Spinner } from 'react-bootstrap';

interface LoginSmsDogrulamaProps {
    tcKimlikNo?: string;
    vergiNo?: string;
    onSmsControlled?: (verified: boolean) => void;
    onSmsSuccess?: (response: any) => void;
    onClose: () => void;
    open: boolean;
    recaptchaRef?: any;
}

const LoginSmsDogrulama = (props: LoginSmsDogrulamaProps) => {
    const [isError, setIsError] = useState(false);
    const [goSmsControl, setGoSmsControl] = useState(false);
    const [seconds, setSeconds] = useState(300);
    const [isActive, setIsActive] = useState(false);
    const [userState, setUserState] = useState<any>(null);
    const [smsCode, setSmsCode] = useState("");
    const [errorContent, setErrorContent] = useState("");
    const [refNo, setRefNo] = useState<any>(undefined);
    const recaptchaTokenRef: any = useRef();
    const navigate = useNavigate();


    useEffect(() => {
        if (goSmsControl) {
            setIsActive(true);
        }
    }, [goSmsControl]);

    function maskPhoneNumber(phoneNumber?: string): string {
        if (!phoneNumber) return "";
        let cleanedNumber = phoneNumber.replace(/\s+/g, "");
        if (cleanedNumber.length === 10 && /^[0-9]{10}$/.test(cleanedNumber)) {
            cleanedNumber = `+90${cleanedNumber}`;
        }
        if (cleanedNumber.length === 13 && cleanedNumber.startsWith("+90")) {
            const countryCode = cleanedNumber.substring(0, 3);
            const firstTwoDigits = cleanedNumber.substring(3, 5);
            const lastTwoDigits = cleanedNumber.substring(cleanedNumber.length - 2);
            return `${countryCode} ${firstTwoDigits}* *** ** ${lastTwoDigits}`;
        }
        return phoneNumber;
    }

    useEffect(() => {
        let interval: any = null;
        if (isActive && seconds > 0) {
            interval = setInterval(() => {
                setSeconds((s) => s - 1);
            }, 1000);
        } else if (seconds === 0) {
            setIsActive(false);
        }
        return () => clearInterval(interval);
    }, [isActive, seconds]);

    const restartTimer = () => {
        setSeconds(60);
        setIsActive(true);
    };

    const validationSchema = yup.object({});

    const handleClose = () => {
        setSmsCode("");
        setIsError(false);
        setErrorContent("");
        setGoSmsControl(false);
        setIsActive(false);
        setUserState(null);
        setRefNo(undefined);
        setSeconds(300);
        props.onClose();
    };

    useEffect(() => {
        if (props.open && (props.tcKimlikNo || props.vergiNo)) {
            sendInitialSms();
            setIsError(false);
            setErrorContent("");
            setGoSmsControl(false);
            setIsActive(false);
            setUserState(null);
            setRefNo(undefined);
            setSeconds(300);
            setSmsCode("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props.tcKimlikNo, props.vergiNo]);

    const sendInitialSms = async () => {
        try {
            // Prop olarak gelen recaptchaRef'i kullan, yoksa kendi ref'ini kullan
            const recaptchaToken = await (props.recaptchaRef?.current || recaptchaTokenRef.current)?.handleSubmit();

            if (recaptchaToken) {
                let request = new SmsDogrulamaRequest(
                    undefined,
                    props.tcKimlikNo,
                    props.vergiNo,
                    undefined,
                    undefined,
                    recaptchaToken,
                    false
                );
                request.isResend = false;
                const res = await SifremiUnuttumAPI.SendSms(request);
                if (res.data.sonuc === 0) {
                    setRefNo(res.data.referenceNumber);
                    setUserState({ cepTelefon: res.data.telefonNumarasi });
                    setGoSmsControl(true);
                } else {
                    setErrorContent(res.data.sonucAciklama || "Bir hata oluştu");
                    setIsError(true);
                    (props.recaptchaRef?.current || recaptchaTokenRef.current)?.reset();
                    setTimeout(() => {
                        setIsError(false);
                        setErrorContent("");
                    }, 4000);
                    handleClose();
                }
            } else {
                setIsError(true);
                setErrorContent("reCAPTCHA doğrulaması gerekli.");
                setTimeout(() => {
                    setIsError(false);
                    setErrorContent("");
                }, 4000);
                handleClose();
            }
        } catch (err: any) {
            const serverMessage = err.response?.data?.message || err.message;
            setErrorContent(serverMessage || "Bir hata oluştu. Lütfen tekrar deneyin.");
            setIsError(true);
            (props.recaptchaRef?.current || recaptchaTokenRef.current)?.reset();
            setTimeout(() => {
                setIsError(false);
                setErrorContent("");
            }, 4000);
            handleClose();
        }
    };


    return (
        <Dialog open={props.open} onClose={handleClose}>
            <Formik initialValues={{}} validationSchema={validationSchema} onSubmit={() => { }}>
                {({ handleSubmit, setSubmitting, isSubmitting }) => {
                    const handleSmsConfirm = () => {
                        setSubmitting(true);
                        let isSuccess = false;
                        if (smsCode.length < 6) {
                            setErrorContent("SMS kodu 6 haneli olmalıdır.");
                            setIsError(true);
                            setTimeout(() => {
                                setIsError(false);
                                setErrorContent("");
                            }, 4000);
                            setSubmitting(false);
                            return;
                        }

                        let request = new SmsDogrulamaKontrolRequest(
                            props.tcKimlikNo || "",
                            refNo,
                            smsCode,
                            props.vergiNo,
                            undefined
                        );

                        SmsKoduKontrolAPI.saveSmsDogrulamaKontrolSave(request)
                            .then((res) => {
                                if (res.data && res.data[0] && res.data[0].sonuc === 0) {
                                    isSuccess = true;
                                    if (typeof props.onSmsControlled === "function") {
                                        props.onSmsControlled(true);
                                    }
                                    if (typeof props.onSmsSuccess === "function") { // onLoginSuccess yerine onSmsSuccess
                                        props.onSmsSuccess(res.data[0]);
                                    }
                                    // handleClose(); // Popup'ı burada kapatma, Login.tsx'te kapatılacak
                                } else {
                                    setErrorContent((res.data && res.data[0] && res.data[0].sonucAciklama) || "Doğrulama başarısız.");
                                    setIsError(true);
                                    setTimeout(() => {
                                        setIsError(false);
                                        setErrorContent("");
                                    }, 4000);
                                }
                            })
                            .catch(() => {
                                setErrorContent("Bir hata oluştu. Lütfen tekrar deneyin.");
                                setIsError(true);
                                setTimeout(() => {
                                    setIsError(false);
                                    setErrorContent("");
                                }, 4000);
                            })
                            .finally(() => {
                                if (!isSuccess) {
                                    setSubmitting(false);
                                }
                            });
                    };

                    return (
                        <Form onSubmit={handleSubmit}>
                            {!goSmsControl ? (
                                <>
                                    <DialogTitle>
                                        SMS Doğrulama Gönderiliyor...
                                        <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                                            <CloseIcon />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent className="pt-3">
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            <Typography>
                                                {props.tcKimlikNo ? `T.C. Kimlik No: ${props.tcKimlikNo}` : `Vergi No: ${props.vergiNo}`} için SMS doğrulama kodu gönderiliyor...
                                            </Typography>

                                            <Collapse in={isError}>
                                                <Alert severity="error">{errorContent}</Alert>
                                            </Collapse>
                                            <Recaptcha ref={recaptchaTokenRef} />
                                        </Box>
                                    </DialogContent>
                                </>
                            ) : (
                                <>
                                    <DialogTitle>
                                        SMS Doğrulama
                                        <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                                            <CloseIcon />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            {isActive ? (
                                                <Typography>
                                                    {userState?.cepTelefon ? `${maskPhoneNumber(userState.cepTelefon)} numaralı telefonunuza SMS kodu gönderildi. Lütfen kodu giriniz.` : 'Kayıtlı telefon bulunamadı.'}
                                                </Typography>
                                            ) : (
                                                <Typography>Kodu girmediniz. Tekrar göndermeyi deneyin.</Typography>
                                            )}
                                            <Collapse in={isError}>
                                                <Alert severity="error">{errorContent}</Alert>
                                            </Collapse>
                                            {isActive && (
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    name="smsCode"
                                                    label="SMS Kodu"
                                                    variant="outlined"
                                                    value={smsCode}
                                                    onChange={(e) => setSmsCode(e.target.value.substring(0, 6))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSmsConfirm();
                                                        }
                                                    }}
                                                />
                                            )}
                                            <Typography variant="h6">{seconds}</Typography>
                                        </Box>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button
                                            onClick={() => {
                                                setSmsCode("");
                                                const request = new SmsDogrulamaRequest(
                                                    userState?.cepTelefon,
                                                    props.tcKimlikNo,
                                                    props.vergiNo,
                                                    undefined,
                                                    undefined,
                                                    undefined,
                                                    false
                                                );
                                                request.isResend = true;
                                                SifremiUnuttumAPI.SendSms(request)
                                                    .then((res) => {
                                                        if (res.data.sonuc === 0) {
                                                            setRefNo(res.data.referenceNumber);
                                                            setGoSmsControl(true);
                                                        } else {
                                                            setErrorContent(res.data.sonucAciklama);
                                                            setIsError(true);
                                                            setTimeout(() => {
                                                                setIsError(false);
                                                                setErrorContent("");
                                                            }, 4000);
                                                        }
                                                    })
                                                    .catch(() => {
                                                        setErrorContent("Bir hata oluştu. Lütfen tekrar deneyin.");
                                                        setIsError(true);
                                                        setTimeout(() => {
                                                            setIsError(false);
                                                            setErrorContent("");
                                                        }, 4000);
                                                    });
                                                restartTimer();
                                            }}
                                            disabled={isActive}
                                        >
                                            Tekrar Gönder
                                        </Button>
                                        <Button onClick={handleSmsConfirm} disabled={isSubmitting}>
                                            {isSubmitting ? <Spinner color='white' /> : "Giriş Yap"}
                                        </Button>
                                    </DialogActions>
                                </>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default LoginSmsDogrulama;
